/**
 * Utility functions for book routing and slug generation
 */

export interface ExpertReview {
  editorName: string;
  editorTitle: string;
  reviewQuote: string;
}

export interface Book {
  id: string;
  slug: string;
  title: string;
  subheading?: string;
  author: string;
  featured: boolean;
  totalDuration: number;
  releaseYear?: number | null;
  description?: string;
  publicationYear?: string | null;
  wordCount?: number;
  renderProfile?: string;
  categories?: string[];
  expertReviews?: ExpertReview[];
  coverUrl?: string;
  coverAnimationUrl?: string | null;
}

/**
 * Convert title to URL slug
 */
export const titleToSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

/**
 * Find book by slug from books array
 */
export const findBookBySlug = (books: Book[], slug: string): Book | undefined => {
  return books.find((book) => book.slug === slug);
};

/**
 * Generate book URL from slug
 */
export const getBookUrl = (slug: string): string => {
  return `/aleph/${slug}`;
};

/** Generate the canonical reader URL for a catalog book. */
export const getReadUrl = (slug: string): string => `/read/${slug}`;

const stripAppPrefix = (path: string): string => {
  const cleanPath = path.split(/[?#]/, 1)[0].replace(/^\/+|\/+$/g, '');
  return cleanPath.replace(/^aleph(?:\/|$)/, '');
};

/** Extract a reader slug from /read/:slug (and the dev /aleph/read/:slug form). */
export const extractReadBookSlugFromPath = (path: string): string | null => {
  const pathWithoutPrefix = stripAppPrefix(path);
  const match = pathWithoutPrefix.match(/^read\/([^/]+)$/);
  return match?.[1] || null;
};

/**
 * Extract book slug from URL path
 */
export const extractBookSlugFromPath = (path: string): string | null => {
  const pathWithoutPrefix = stripAppPrefix(path);

  // The reader is its own route namespace, never a book-detail slug.
  if (pathWithoutPrefix === 'read' || pathWithoutPrefix.startsWith('read/')) {
    return null;
  }

  // Check if it's a book slug (not a reserved route)
  const reservedRoutes = ['library', 'chat', 'signup', 'email-verification', 'login', 'profile'];
  if (reservedRoutes.includes(pathWithoutPrefix)) {
    return null;
  }

  return pathWithoutPrefix || null;
};
